import { useCallback, useEffect, useRef, useState } from 'react';
import { api, errorText, formatDate, formatMoney } from '../api';
import { Badge, Card, Empty, Loading, Notice } from '../components/ui';

interface BankUmsatz {
  id: string;
  buchungstag: string;
  betrag_cents: number;
  waehrung: string;
  verwendungszweck: string | null;
  gegen_name: string | null;
  referenz: string | null;
  status: string;
  vorschlag_rechnung_id: string | null;
  vorschlag_nummer: string | null;
  rechnung_id: string | null;
  rechnung_nummer: string | null;
}

interface OffenerPosten {
  id: string;
  number: string;
  owner_name: string;
  open_cents: number;
}

export function BankingPage() {
  const [umsaetze, setUmsaetze] = useState<BankUmsatz[] | null>(null);
  const [offene, setOffene] = useState<OffenerPosten[]>([]);
  const [filter, setFilter] = useState<'offen' | 'zugeordnet' | 'ignoriert' | ''>('offen');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const dateiRef = useRef<HTMLInputElement>(null);

  const laden = useCallback(async () => {
    setError('');
    try {
      const [u, r] = await Promise.all([
        api<BankUmsatz[]>('/api/banking/umsaetze', { query: { status: filter || undefined } }),
        api<{ items: OffenerPosten[] }>('/api/billing/receivables'),
      ]);
      setUmsaetze(u);
      setOffene(r.items);
    } catch (err) {
      setError(errorText(err));
    }
  }, [filter]);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function importieren(datei: File) {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const xml = await datei.text();
      const r = await api<{ eingelesen: number; neu: number; dubletten: number; mit_vorschlag: number }>(
        '/api/banking/import',
        { method: 'POST', body: { xml } },
      );
      setMessage(
        `${r.neu} neue Umsätze (${r.dubletten} Dubletten übersprungen), ${r.mit_vorschlag} mit Zuordnungsvorschlag.`,
      );
      if (dateiRef.current) dateiRef.current.value = '';
      await laden();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function zuordnen(umsatzId: string, rechnungId: string) {
    if (!rechnungId) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/banking/umsaetze/${umsatzId}/zuordnen`, {
        method: 'POST',
        body: { rechnungId },
      });
      setMessage('Zahlung gebucht.');
      await laden();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function ignorieren(umsatzId: string) {
    setBusy(true);
    try {
      await api(`/api/banking/umsaetze/${umsatzId}/ignorieren`, { method: 'POST' });
      await laden();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <Card title="Kontoauszug einlesen (CAMT.053)">
        <div className="small muted" style={{ marginBottom: 12 }}>
          Die CAMT.053-Datei aus dem Online-Banking hochladen. Zahlungseingänge werden erkannt und —
          wenn Rechnungsnummer und Betrag passen — der offenen Rechnung vorgeschlagen. Gebucht wird
          erst nach Bestätigung. Dieselbe Datei erneut einzulesen legt nichts doppelt an.
        </div>
        <input
          ref={dateiRef}
          type="file"
          accept=".xml,text/xml,application/xml"
          disabled={busy}
          onChange={(e) => {
            const datei = e.target.files?.[0];
            if (datei) void importieren(datei);
          }}
        />
      </Card>

      <Card
        title="Umsätze"
        padded={false}
        actions={
          <select value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)}>
            <option value="offen">offen</option>
            <option value="zugeordnet">zugeordnet</option>
            <option value="ignoriert">ignoriert</option>
            <option value="">alle</option>
          </select>
        }
      >
        {!umsaetze ? (
          <Loading />
        ) : umsaetze.length === 0 ? (
          <Empty>Keine Umsätze in dieser Ansicht.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Zahler / Empfänger</th>
                  <th>Verwendungszweck</th>
                  <th className="num">Betrag</th>
                  <th>Zuordnung</th>
                </tr>
              </thead>
              <tbody>
                {umsaetze.map((u) => (
                  <tr key={u.id}>
                    <td className="small muted">{formatDate(u.buchungstag)}</td>
                    <td className="small">{u.gegen_name ?? '—'}</td>
                    <td className="small" style={{ maxWidth: 320 }}>{u.verwendungszweck ?? '—'}</td>
                    <td className="num" style={{ color: u.betrag_cents < 0 ? 'var(--bad)' : undefined }}>
                      {u.betrag_cents > 0 ? '+' : ''}
                      {formatMoney(u.betrag_cents)}
                    </td>
                    <td>
                      {u.status === 'zugeordnet' ? (
                        <Badge kind="ok">gebucht auf {u.rechnung_nummer}</Badge>
                      ) : u.status === 'ignoriert' ? (
                        <Badge kind="default">ignoriert</Badge>
                      ) : u.betrag_cents <= 0 ? (
                        <span className="small muted">Ausgang — später SEPA/Handzuordnung</span>
                      ) : (
                        <Zuordnung
                          umsatz={u}
                          offene={offene}
                          busy={busy}
                          onZuordnen={(rid) => zuordnen(u.id, rid)}
                          onIgnorieren={() => ignorieren(u.id)}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

function Zuordnung({
  umsatz,
  offene,
  busy,
  onZuordnen,
  onIgnorieren,
}: {
  umsatz: BankUmsatz;
  offene: OffenerPosten[];
  busy: boolean;
  onZuordnen: (rechnungId: string) => void;
  onIgnorieren: () => void;
}) {
  const [wahl, setWahl] = useState('');
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {umsatz.vorschlag_rechnung_id && (
        <button
          className="primary small"
          disabled={busy}
          onClick={() => onZuordnen(umsatz.vorschlag_rechnung_id!)}
        >
          Vorschlag {umsatz.vorschlag_nummer} übernehmen
        </button>
      )}
      <select value={wahl} onChange={(e) => setWahl(e.target.value)}>
        <option value="">— Rechnung wählen —</option>
        {offene.map((o) => (
          <option key={o.id} value={o.id}>
            {o.number} · {o.owner_name} · {formatMoney(o.open_cents)}
          </option>
        ))}
      </select>
      <button className="small" disabled={busy || !wahl} onClick={() => onZuordnen(wahl)}>
        buchen
      </button>
      <button className="small ghost" disabled={busy} onClick={onIgnorieren}>
        ignorieren
      </button>
    </div>
  );
}
